/** A member in a published or tentative lineup (player or RSVP guest). */
export interface LineupMember {
  key: string;
  name: string;
  playerId?: number;
  guestRsvpId?: string;
  guestName?: string;
  balanceScore: number;
}

export type StoredLineupEntry =
  | number
  | {
      playerId?: number;
      guestRsvpId?: string;
      guestName?: string;
      name?: string;
      balanceScore?: number;
    };

export interface EventLineup {
  teamA: StoredLineupEntry[];
  teamB: StoredLineupEntry[];
}

export interface ParsedLineup {
  teamA: LineupMember[];
  teamB: LineupMember[];
  playerIdsA: number[];
  playerIdsB: number[];
}

export function lineupMemberKey(member: Pick<LineupMember, "playerId" | "guestRsvpId">): string {
  if (member.playerId != null) return `player-${member.playerId}`;
  if (member.guestRsvpId) return `guest-${member.guestRsvpId}`;
  throw new Error("Lineup member requires playerId or guestRsvpId");
}

export function parseStoredLineupEntry(
  entry: StoredLineupEntry,
  context: {
    playerNameById: Map<number, string>;
    guestNameByRsvpId?: Map<string, string>;
    defaultBalanceScore?: number;
  }
): LineupMember | null {
  const defaultScore = context.defaultBalanceScore ?? 75;

  if (typeof entry === "number") {
    const name = context.playerNameById.get(entry) ?? `Player ${entry}`;
    return {
      key: `player-${entry}`,
      name,
      playerId: entry,
      balanceScore: defaultScore,
    };
  }

  if (entry.playerId != null) {
    const name = entry.name ?? context.playerNameById.get(entry.playerId) ?? `Player ${entry.playerId}`;
    return {
      key: `player-${entry.playerId}`,
      name,
      playerId: entry.playerId,
      balanceScore: entry.balanceScore ?? defaultScore,
    };
  }

  if (entry.guestRsvpId) {
    const name =
      entry.guestName ??
      entry.name ??
      context.guestNameByRsvpId?.get(entry.guestRsvpId) ??
      "Invitado";
    if (!name.trim()) return null;
    return {
      key: `guest-${entry.guestRsvpId}`,
      name,
      guestRsvpId: entry.guestRsvpId,
      guestName: name,
      balanceScore: entry.balanceScore ?? defaultScore,
    };
  }

  return null;
}

export function parseEventLineup(
  lineup: EventLineup | { teamA?: StoredLineupEntry[]; teamB?: StoredLineupEntry[] } | null | undefined,
  context: {
    playerNameById: Map<number, string>;
    guestNameByRsvpId?: Map<string, string>;
    defaultBalanceScore?: number;
  }
): ParsedLineup {
  const teamA = (lineup?.teamA ?? [])
    .map((entry) => parseStoredLineupEntry(entry, context))
    .filter((m): m is LineupMember => m != null);
  const teamB = (lineup?.teamB ?? [])
    .map((entry) => parseStoredLineupEntry(entry, context))
    .filter((m): m is LineupMember => m != null);

  return {
    teamA,
    teamB,
    playerIdsA: teamA.map((m) => m.playerId).filter((id): id is number => id != null),
    playerIdsB: teamB.map((m) => m.playerId).filter((id): id is number => id != null),
  };
}

export function serializeLineupMember(member: LineupMember): StoredLineupEntry {
  if (member.playerId != null) {
    return {
      playerId: member.playerId,
      name: member.name,
      balanceScore: member.balanceScore,
    };
  }
  if (member.guestRsvpId) {
    return {
      guestRsvpId: member.guestRsvpId,
      guestName: member.guestName ?? member.name,
      balanceScore: member.balanceScore,
    };
  }
  throw new Error("Cannot serialize lineup member without playerId or guestRsvpId");
}

export function serializeLineupSide(members: LineupMember[]): StoredLineupEntry[] {
  return members.map(serializeLineupMember);
}

export function lineupMembersToTeamPlayerVM(
  members: LineupMember[],
  side: "a" | "b",
  currentUserPlayerId?: number | null
): { key: string; name: string; isCurrentUser: boolean }[] {
  return members.map((m) => ({
    key: `${side}-${m.key}`,
    name: m.name,
    isCurrentUser: currentUserPlayerId != null && m.playerId === currentUserPlayerId,
  }));
}
